// ===================================
// SESSION DETAIL PAGE
// src/pages/SessionDetail.js
// ===================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSession } from '../services/sessionsApi';
import MessageContent from '../components/MessageContent';
import RecommendationCard, { isRecommendationMessage } from '../components/RecommendationCard';
import { generateTranscriptPDF } from '../utils/pdfGenerator';


import './SessionDetail.css';


const SessionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSession();
  }, [id]);

  const loadSession = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSession(id);
      
      if (!data || !data.id) {
        setError('Session not found');
        return;
      }
      
      setSession(data);
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Session not found');
    } finally {
      setLoading(false);
    }
  };

  // Add this helper function after getScoreClass
const isAcademyGuidance = () => {
  return session?.category_slug === 'academy-guidance';
};

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'badge-success',
      active: 'badge-warning',
      paid: 'badge-info',
      pending: 'badge-neutral',
      expired: 'badge-error'
    };
    return badges[status] || 'badge-neutral';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getScore = () => {
    if (!session) return null;
    return session.overall_score || session.score?.overall || null;
  };

  const getScoreBreakdown = () => {
    if (!session) return null;
    return session.score_breakdown || session.score?.breakdown || null;
  };

  const getFeedback = () => {
    if (!session || !session.feedback) return null;
    try {
      return typeof session.feedback === 'string' 
        ? JSON.parse(session.feedback) 
        : session.feedback;
    } catch {
      return null;
    }
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'average';
    return 'poor';
  };

  if (loading) {
    return (
      <div className="session-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="session-detail-page">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h2>Session not found</h2>
          <button onClick={() => navigate('/sessions')} className="btn btn-primary">
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  const score = getScore();
  const scoreBreakdown = getScoreBreakdown();
  const feedback = getFeedback();

  const handleDownloadPDF = () => {
  if (session) {
    generateTranscriptPDF(session);
  }
};

  return (
    <div className="session-detail-page">
      <div className="session-detail-container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/sessions">Sessions</Link>
          <span>/</span>
          <span>Session Details</span>
        </nav>

        {/* Header */}
        <div className="detail-header">
          <div className="header-info">
            <h1>{session.interviewer_personality} Interview</h1>
            <div className="header-meta">
              <span className={`badge ${getStatusBadge(session.status)}`}>
                {session.status}
              </span>
              <span>{session.duration} min</span>
              <span>{session.experience_level}</span>
              <span>{session.interview_type || 'text'}</span>
            </div>
          </div>
          
          {/* {score && (
            <div className={`score-circle large ${getScoreClass(score)}`}>
              <span className="score-value">{score}</span>
              <span className="score-label">Score</span>
            </div>
          )} */}
          {/* Hide score for academy guidance sessions */}
{score && !isAcademyGuidance() && (
  <div className={`score-circle large ${getScoreClass(score)}`}>
    <span className="score-value">{score}</span>
    <span className="score-label">Score</span>
  </div>
)}
        </div>

        {/* Ready to Start - for paid sessions */}
        {session.status === 'paid' && (
          <div className="ready-card">
            <div className="ready-content">
              <h3>🎬 Ready to Start</h3>
              
              {session.access_code && (
                <div className="access-code-display">
                  <span className="code-label">Access Code:</span>
                  <span className="code-value">{session.access_code}</span>
                </div>
              )}
              
              {/* Different UI for phone vs text/voice */}
              {session.interview_type === 'phone' ? (
                <div className="phone-instructions">
                  <h4>📞 Phone Interview Instructions</h4>
                  <div className="phone-number">
                    <span className="number-label">Call this number:</span>
                    <span className="number-value">
                      {process.env.REACT_APP_PHONE_NUMBER || '+1 (555) 123-4567'}
                    </span>
                  </div>
                  <ol className="phone-steps">
                    <li>Call the number above</li>
                    <li>When prompted, enter your access code: <strong>{session.access_code}</strong></li>
                    <li>Your AI interview will begin automatically</li>
                  </ol>
                  <p className="phone-note">
                    Note: Phone interviews are conducted via voice call, not through this website.
                  </p>
                </div>
              ) : (
                <button 
                  onClick={() => navigate(`/interview/${session.id}`)}
                  className="btn btn-primary btn-lg"
                >
                  Start {session.interview_type === 'voice' ? 'Voice' : 'Text'} Interview
                </button>
              )}
            </div>
          </div>
        )}

        {/* Active Session */}
        {session.status === 'active' && (
          <div className="active-card">
            <h3>🎙️ Interview in Progress</h3>
            <p>You have an active interview session.</p>
            <button 
              onClick={() => navigate(`/interview/${session.id}`)}
              className="btn btn-primary"
            >
              Continue Interview
            </button>
          </div>
        )}

        {/* Completed Session - Show Results */}
        {session.status === 'completed' && (
          <div className="results-section">
            {/* Score Breakdown */}
{scoreBreakdown && !isAcademyGuidance() && (
              <div className="breakdown-card">
                <h3>Score Breakdown</h3>
                <div className="breakdown-grid">
                  {Object.entries(scoreBreakdown).map(([key, value]) => (
                    <div key={key} className="breakdown-item">
                      <div className="breakdown-header">
                        <span className="breakdown-label">{key}</span>
                        <span className="breakdown-value">{value}%</span>
                      </div>
                      <div className="breakdown-bar">
                        <div 
                          className={`breakdown-fill ${getScoreClass(value)}`}
                          style={{ width: `${value}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Feedback */}
            {feedback && (
              <div className="feedback-card">
                <h3>Feedback</h3>
                
                {feedback.summary && (
                  <div className="feedback-summary">
                    <p>{feedback.summary}</p>
                  </div>
                )}
                
                {feedback.strengths && feedback.strengths.length > 0 && (
                  <div className="feedback-section">
                    <h4>✅ Strengths</h4>
                    <ul>
                      {feedback.strengths.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {feedback.improvements && feedback.improvements.length > 0 && (
                  <div className="feedback-section">
                    <h4>📈 Areas for Improvement</h4>
                    <ul>
                      {feedback.improvements.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Transcript */}
           {session.transcript && session.transcript.length > 0 && (
  <div className="transcript-card">
    <div className="transcript-header">
      <h3>Interview Transcript</h3>
      <button 
        className="btn btn-secondary btn-sm download-btn"
        onClick={handleDownloadPDF}
        title="Download transcript as PDF"
      >
        📥 Download PDF
      </button>
    </div>
                <div className="transcript-messages">
            {session.transcript.map((msg, i) => (
  <div key={i} className={`transcript-msg ${msg.role}`}>
    <span className="msg-role">
      {msg.role === 'user' ? '👤 You' : '🤖 Interviewer'}
    </span>
    {/* Check if this is a recommendation message */}
    {msg.role === 'assistant' && isRecommendationMessage(msg.content) ? (
      <RecommendationCard content={msg.content} />
    ) : (
      <p>{msg.content}</p>
    )}
  </div>
))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Session Info */}
        <div className="session-info-card">
          <h3>Session Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Created</span>
              <span className="info-value">{formatDate(session.created_at)}</span>
            </div>
            {session.started_at && (
              <div className="info-item">
                <span className="info-label">Started</span>
                <span className="info-value">{formatDate(session.started_at)}</span>
              </div>
            )}
            {session.ended_at && (
              <div className="info-item">
                <span className="info-label">Ended</span>
                <span className="info-value">{formatDate(session.ended_at)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetail;